import {defineStore} from "pinia";
import {useRoute} from "vue-router";
import {computed, ref, watch} from "vue";
import {Photo, ReportPhoto} from "../models/task";
import {MaybeComputedRef, resolveUnref} from "@vueuse/core";
import {
    useDeletePhoto,
    useTaskPhotos,
    usePerformTaskPhotos,
    useUpdatePhotoDate,
    useUpdatePhotoCaption, useUpdatePhotoOnlyDate, useUpdateOnlyPhotoLocation
} from "../composables/task";
import {useDayJs} from "../composables/dayjs";
import * as Sentry from "@sentry/vue";
import {photoCdnUrl} from "../util/photoCdn";

export const usePhotoStore = defineStore('photos', () => {
    const route = useRoute()
    const dayJs = useDayJs()

    const photosUrl = computed(() => {
        return photos.value.map(p => {
            return photoCdnUrl(p.address, {
                preview: '960x960',
                formatBeforeCaption: 'jpeg',
                caption: p.caption ? {text: p.caption, fontSize: 30} : null,
            })
        })
    })
    const projectId = computed(() => Number(route.params.projectId))
    const taskId = computed(() => Number(route.params.taskId))
    const performTaskId = computed(() => Number(route.params.performTaskId))

    const photos = ref<ReportPhoto[]>([])
    const open = ref(false)
    const shared = ref(false)
    const last = ref(false)
    const loading = ref(false)
    const visibleViewer = ref(false)
    const viewedImage = ref(0)

    const viewedImageDate = computed(() => dayJs.utc(photos.value[viewedImage.value].createdAt).local().format("YYYY-MM-DD HH:mm"))
    const viewedCustomLocation = computed(() => photos.value[viewedImage.value].customLocation)


    watch(open, (val) => {
        if (!val) {
            photos.value = []
            last.value = false
        }
    })

    watch([projectId, taskId], (val) => {
        if (open.value) {
            open.value = false
        }
    })

    async function load(page: MaybeComputedRef<number>) {
        loading.value = true
        const {data} = route.name === 'TaskInfo' ? await useTaskPhotos(projectId, taskId, page) : await usePerformTaskPhotos(projectId, performTaskId, page)
        const fetchedPhotos = data.value?.content;
        photos.value = photos.value.concat(fetchedPhotos ?? [])
        last.value = data.value?.last ?? true
        loading.value = false
        return last.value
    }

    async function deletePhoto(photo: MaybeComputedRef<Photo>) {
        await useDeletePhoto(projectId, resolveUnref(photo).id)
        // Delete photo from array
        photos.value = photos.value.filter(p => p.id !== resolveUnref(photo).id)
        if (viewedImage.value >= photos.value.length) {
            viewedImage.value = photos.value.length - 1
        }
    }

    async function updateViewedPhotoDate(date: MaybeComputedRef<string>) {
        Sentry.addBreadcrumb({
            category: 'photo',
            message: 'updateViewedPhotoDate',
            data: {
                date: resolveUnref(date)
            }
        })
        const isoDate = dayJs(resolveUnref(date)).utc().format()
        await useUpdatePhotoDate(projectId, photos.value[viewedImage.value].id, isoDate)
        photos.value[viewedImage.value].createdAt = isoDate
    }


    async function updateUseCaption(caption: MaybeComputedRef<boolean>) {
        await useUpdatePhotoCaption(projectId, photos.value[viewedImage.value].id, caption)
        photos.value[viewedImage.value].usedCaption = resolveUnref(caption)
    }



    async function updatePhotoUseCaption(photo: MaybeComputedRef<Photo>, caption: MaybeComputedRef<boolean>) {
        const targetPhoto = resolveUnref(photo)
        await useUpdatePhotoCaption(projectId, targetPhoto.id, caption)
        const index = photos.value.findIndex(p => p.id === targetPhoto.id)
        if (index !== -1) {
            photos.value[index].usedCaption = resolveUnref(caption)
        }
    }

    // Date in format YYYY-MM-DD
    async function updateOnlyPhotoDate(photo: MaybeComputedRef<Photo>, date: MaybeComputedRef<string>) {
        const targetPhoto = resolveUnref(photo)
        await useUpdatePhotoOnlyDate(projectId, targetPhoto.id, date)
        // Update photo date-time in the array to match the new date
        const newDate = dayJs(resolveUnref(date))
        const index = photos.value.findIndex(p => p.id === targetPhoto.id)
        if (index === -1) {
            return
        }
        let currentDate = dayJs.utc(photos.value[index].createdAt)
        currentDate = currentDate.set('year', newDate.year())
        currentDate = currentDate.set('month', newDate.month())
        currentDate = currentDate.set('date', newDate.date())
        photos.value[index].createdAt = currentDate.utc().format()
    }

    async function updatePhotoLocation(photo: MaybeComputedRef<Photo>, latitude: MaybeComputedRef<number>, longitude: MaybeComputedRef<number>) {
        await useUpdateOnlyPhotoLocation(projectId, photo, latitude, longitude)
        // Update photo date-time in the array to match the new date
        const targetPhoto = resolveUnref(photo)
        const index = photos.value.findIndex(p => p.id === targetPhoto.id)
        if (index !== -1) {
            photos.value[index].latitude = resolveUnref(latitude)
            photos.value[index].longitude = resolveUnref(longitude)
        }
    }

    return {
        open,
        shared,
        visibleViewer,
        viewedImageDate,
        viewedCustomLocation,
        updatePhotoUseCaption,
        updateViewedPhotoDate,
        updateOnlyPhotoDate,
        updatePhotoLocation,
        viewedImage,
        updateUseCaption,
        photosUrl,
        load,
        photos,
        deletePhoto,
        loading,
        last
    }
})
